<template>
	<AppNotification v-for="notification in activeNotifications" :key="notification.ref" :type="typeFromObject(notification)" :title="getString(notification.title)" :body-text="getString(notification.body, notification.bodyParams ? notification.bodyParams : [])" :duration="notification.duration ? notification.duration : 4300" :ref="notification.ref" :id="notification.ref" />
</template>

<script>
import AppNotification from "@/shared/components/AppNotification.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";

export default {
	setup() {
		const notificationStore = useNotificationStore();
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		return {notificationStore, getString: languageStore.getString, userStore};
	},
	name: "NotificationManager",
	components: {AppNotification},
	data() {
		return {
			activeNotifications: [],
		}
	},
	computed: {
		notifications() {
			return [
				{ref: 'generic-success', title: 'saved', body: 'changes_saved'},
				{ref: 'generic-fail', title: 'error', body: 'try_later_error'},
				{ref: 'generic-error', title: 'error', body: 'try_later_error'},
				{ref: 'invalid_otp', title: 'error', body: 'invalid_otp_code'},
				{ref: 'domain-success', title: 'saved', body: 'success_saved_domain'},
				{ref: 'domain-dns-fail', title: 'error', body: 'dns_warn', duration: 6000},
				{ref: 'menu-max-pages-fail', title: 'error', body: 'menu_max_pages', bodyParams: ['5']},
				{ref: 'menu-name-too-short-fail', title: 'error', body: 'menu_name_too_short'},
				{ref: 'new-menu-name-too-short-fail', title: 'error', body: 'x_name_too_short', bodyParams: [this.userStore.menuWord]},
				{ref: 'menu-url-too-short-fail', title: 'error', body: 'menu_url_too_short'},
				{ref: 'upload-file-too-big', title: 'error', body: 'upload_error_file_too_big', duration: 6000},
				{ref: 'error-parsing-file', title: 'error', body: 'error_parsing_file', duration: 6000},
				{ref: 'deepen-empty-url-error', title: 'error', body: 'deepen_empty_url_error'},
				{ref: 'invalid-url', title: 'error', body: 'invalid_url'},
				{ref: 'invalid-url-custom', title: 'error', body: 'invalid_url_custom'},
				{ref: 'invalid-analytics-field', title: 'error', body: 'invalid_analytics_field'},
				{ref: 'timetable-invalid', title: 'error', body: 'timetable_invalid'},
				{ref: 'timetable-overlaps', title: 'error', body: 'timetable_overlaps'},
				{ref: 'timetable-invalid-time', title: 'error', body: 'timetable_invalid_time'},
				{ref: 'timetable-save-item', title: 'error', body: 'timetable_save_item_before'},
				{ref: 'timetable-save-category', title: 'error', body: 'timetable_save_category_before'},
				{ref: 'invalid-timeslot-time', title: 'error', body: 'invalid_timeslot_time'},
				{ref: 'invalid-timeslot-interval', title: 'error', body: 'invalid_timeslot_interval'},
				{ref: 'composed-save-item', title: 'error', body: 'composed_save_item_before'},
				{ref: 'composed-list-name', title: 'warning', body: 'composed_empty_list_name', duration: 6000},
				{ref: 'composed-save-list', title: 'warning', body: 'composed_save_list_before'},
				{ref: 'composed-saved-list', title: 'saved', body: 'composed_list_saved'},
				{ref: 'composed-missing-name', title: 'error', body: 'composed_missing_name'},
				{ref: 'multi-order-restriction', title: 'warning', body: 'multi_order_restriction'},
				{ref: 'composed-list-product-added', title: 'added', body: 'composed_list_product_added'},
				{ref: 'composed-no-items', title: 'error', body: 'composed_no_items'},
				{ref: 'item-name-invalid', title: 'error', body: 'item_name_invalid'},
				{ref: 'plugin-name-invalid', title: 'error', body: 'plugin_name_invalid'},
				{ref: 'plugin-url-invalid', title: 'error', body: 'plugin_url_invalid'},
				{ref: 'table-no-orders', title: 'error', body: 'table_no_orders'},
				{ref: 'table-no-data', title: 'warning', body: 'table_no_data'},
				{ref: 'item-price-discount-invalid', title: 'error', body: 'item_price_discount_invalid', duration: 5500},
				{ref: 'item-price-mode', title: 'error', body: 'item_price_mode', duration: 6000},
				{ref: 'item-slug-error', title: 'error', body: 'products_links_equal_warn', duration: 6000},
				{ref: 'delivery-status-no-phone', title: 'error', body: 'missing_company_data', duration: 8000},
				{ref: 'item-inserted', title: 'saved', body: 'item_inserted'},
				{ref: 'auth-denied-error', title: 'error', body: 'auth_denied_error'},
				{ref: 'item-updated', title: 'saved', body: 'item_updated'},
				{ref: 'category-saved', title: 'saved', body: 'category_saved'},
				{ref: 'category-saved-for-new-item', title: 'saved', body: 'category_saved_new_item'},
				{ref: 'menu-saved', title: 'saved', body: 'menu_saved', bodyParams: [this.userStore.menuWord]},
				{ref: 'too-many-duplicate', title: 'warning', body: 'too_many_duplicate', duration: 6000},
				{ref: 'no-target-category-selected', title: 'warning', body: 'no_target_category_selected', duration: 6000},
				{ref: 'duplicated-product-success', title: 'saved', body: 'duplicated_item'},
				{ref: 'duplicated-products-success', title: 'saved', body: 'duplicated_items'},
				{ref: 'moved-product-success', title: 'success', body: 'moved_item'},
				{ref: 'moved-products-success', title: 'success', body: 'moved_items'},
				{ref: 'partner-subscription', title: 'success', body: 'partner_subscription'},
				{ref: 'report-printed', title: 'success', body: 'report_printed'},
				{ref: 'availability-changed', title: 'success', body: 'availability_changed'},
				{ref: 'you-will-be-contacted-soon', title: 'success', body: 'you_will_be_contacted_soon', duration: 10000},
				{ref: 'campaigns-interest-registered', title: 'success', body: 'campaigns_interest_registered', duration: 8000},
				{ref: 'price-changed', title: 'success', body: 'price_changed'},
				{ref: 'discount-applied', title: 'success', body: 'discount_applied'},
				{ref: 'full-price-low', title: 'warning', body: 'full_price_lower_than_price', duration: 6000},
				{ref: 'cover-change-warn-additional-settings', title: 'warning', body: 'cover_change_warn_additional_settings'},
				{ref: 'at-least-one-payment-method', title: 'error', body: 'at_least_one_payment_method'},
				{ref: 'fees-customer-only-cards', title: 'error', body: 'fees_customer_only_cards', duration: 6000},
				{ref: 'beta-code-required', title: 'error', body: 'beta_code_required', duration: 6000},
				{ref: 'external-location-id-invalid', title: 'error', body: 'external_location_id_invalid'},
				{ref: 'order-pay-needed', title: 'error', body: 'order_pay_needed', duration: 6000},
				{ref: 'btc-password-empty', title: 'error', body: 'btc_password_empty', duration: 6000},
				{ref: 'btc-error-1', title: 'error', body: 'btc_error_1', duration: 8000},
				{ref: 'select-language', title: 'error', body: 'select_language_error'},
				{ref: 'select-sound', title: 'error', body: 'select_sound'},
				{ref: 'invalid-name', title: 'error', body: 'error_invalid_name'},
				{ref: 'invalid-date', title: 'error', body: 'error_invalid_date'},
				{ref: 'invalid-time', title: 'error', body: 'error_invalid_time'},
				{ref: 'webhook-limit', title: 'error', body: 'webhook_limit'},
				{ref: 'webhook-exists', title: 'error', body: 'webhook_exists'},
				{ref: 'no-elements-error', title: 'error', body: 'no_elements_error'},
				{ref: 'too-many-elements-error', title: 'error', body: 'too_many_elements_error'},
				{ref: 'menu-delete-last', title: 'error', body: 'menu_delete_last'},
				{ref: 'saved-language', title: 'saved', body: 'saved_language'},
				{ref: 'saved-integration', title: 'saved', body: 'saved_integration'},
				{ref: 'invoice-company-name-invalid', title: 'error', body: 'invoice_company_name_invalid'},
				{ref: 'invoice-switch-customer-missing', title: 'warning', body: 'select_target_customer_missing'},
				{ref: 'invoice-switch-same-customer', title: 'warning', body: 'select_new_target_customer'},
				{ref: 'invoice-retry-success', title: 'save', body: 'invoice_retry_success'},
				{ref: 'invoice-retry-failure', title: 'error', body: 'invoice_retry_failure'},
				{ref: 'last-course-reached', title: 'warning', body: 'last_course_reached'},
				{ref: 'associated-already', title: 'error', body: 'associated_already'},
				{ref: 'invoice-vatid-invalid', title: 'error', body: 'invoice_vatid_invalid'},
				{ref: 'invoice-address-invalid', title: 'error', body: 'invoice_address_invalid'},
				{ref: 'invoice-sdi-invalid', title: 'error', body: 'invoice_sdi_invalid'},
				{ref: 'invoice-request-success', title: 'saved', body: 'invoice_request_success'},
				{ref: 'invoice-request-failed', title: 'error', body: 'invoice_request_failed', duration: 8000},
				{ref: 'error-short-password', title: 'error', body: 'short_password', duration: 6000},
				{ref: 'coupon-code-exists', title: 'error', body: 'coupon_code_exists'},
				{ref: 'coupon-additional-code-exists', title: 'error', body: 'code_already_exists'},
				{ref: 'insert-valid-code', title: 'error', body: 'insert_valid_code'},
				{ref: 'coupon-code-invalid', title: 'error', body: 'invalid_coupon_code', duration: 6000},
				{ref: 'coupon-mode', title: 'warning', body: 'coupon_mode'},
				{ref: 'contact-team-to-perform-action', title: 'error', body: 'contact_team_to_perform_action', duration: 6000},
				{ref: 'error-delete-business-orders', title: 'error', body: 'business_has_orders_cant_delete', duration: 6000},
				{ref: 'error-delete-business-subscribed', title: 'error', body: 'business_subscribed_cant_delete', duration: 6000},
				{ref: 'nothing-to-pay', title: 'warning', body: 'nothing_to_pay'},
				{ref: 'wrong-password', title: 'error', body: 'wrong_password', duration: 6000},
				{ref: 'business-not-allowed', title: 'error', body: 'business_not_allowed', duration: 6000},
				{ref: 'contract-unsigned', title: 'error', body: 'contract_unsigned', duration: 6000},
				{ref: 'coupon-missing-name', title: 'error', body: 'coupon_missing_name'},
				{ref: 'coupon-missing-code', title: 'error', body: 'coupon_missing_code'},
				{ref: 'coupon-missing-value', title: 'error', body: 'coupon_missing_value'},
				{ref: 'cash-limit-exceeded', title: 'error', body: 'cash_limit_exceeded'},
				{ref: 'wrong-email', title: 'error', body: 'invalid_email_info', duration: 6000},
				{ref: 'wrong-phone', title: 'error', body: 'invalid_phone', duration: 6000},
				{ref: 'wrong-date', title: 'error', body: 'invalid_date', duration: 6000},
				{ref: 'wrong-fiscal', title: 'error', body: 'invalid_fiscal', duration: 6000},
				{ref: 'invalid-people', title: 'error', body: 'invalid_people', duration: 6000},
				{ref: 'error-no-admin', title: 'error', body: 'error_no_admin', duration: 6000},
				{ref: 'invalid-experience', title: 'error', body: 'invalid_experience'},
				{ref: 'saved-published', title: 'saved', body: 'saved_published_changes'},
				{ref: 'user-invited', title: 'user_invited', body: 'user_invited_info', type: 1, duration: 8000},
				{ref: 'no-user', title: 'error', body: 'no_user'},
				{ref: 'no-permissions', title: 'error', body: 'no_permissions'},
				{ref: 'already-has-permissions', title: 'error', body: 'already_has_permissions'},
				{ref: 'self-user', title: 'error', body: 'self_user'},
				{ref: 'already-in-users-temp-permission-warn', title: 'error', body: 'already_in_users_temp_permission_warn', duration: 6000},
				{ref: 'error-email-send', title: 'error', body: 'error_email_send', duration: 6000},
				{ref: 'error-address', title: 'error', body: 'invalid_address', duration: 6000},
				{ref: 'invalid-cardholder', title: 'error', body: 'invalid_cardholder', duration: 6000},
				{ref: 'reached-max-cards', title: 'error', body: 'reached_max_cards', duration: 6000},
				{ref: 'no-active-plan', title: 'error', body: 'no_active_plan', duration: 6000},
				{ref: 'no-transactions', title: 'error', body: 'no_transactions', duration: 6000},
				{ref: 'error-fill-fields', title: 'error', body: 'fill_every_field', duration: 8000},
				{ref: 'error-fill-fields-continue', title: 'error', body: 'fill_every_field_continue', duration: 8000},
				{ref: 'duplicate-printer', title: 'error', body: 'printer_not_associable', duration: 6000},
				{ref: 'printer-sn-error', title: 'error', body: 'printer_sn_error', duration: 8000},
				{ref: 'no-printers-found', title: 'error', body: 'no_printers_found'},
				{ref: 'invalid-response', title: 'error', body: 'invalid_response'},
				{ref: 'external-plugin-not-paid', title: 'error', body: 'external_plugin_not_paid'},
				{ref: 'saved-permissions', title: 'saved', body: 'saved_permissions'},
				{ref: 'saved-pec', title: 'saved', body: 'saved_pec'},
				{ref: 'saved-business-address', title: 'saved', body: 'saved_address'},
				{ref: 'deleted-business', title: 'saved', body: 'deleted_business'},
				{ref: 'error-no-intent-to-pay', title: 'error', body: 'no_intent_to_pay'},
				{ref: 'edit-order-error-paid', title: 'error', body: 'edit_order_error_paid'},
				{ref: 'edit-order-error-partially-paid', title: 'error', body: 'edit_order_error_partially_paid', duration: 8000},
				{ref: 'split-overflow', title: 'error', body: 'split_overflow'},
				{ref: 'stripe-failed', title: 'warning', body: 'stripe_failed'},
				{ref: 'stripe-sub-base', title: 'success', body: 'stripe_sub_base', duration: 8000},
				{ref: 'message-sent', title: 'success', body: 'message_has_been_sent'},
				{ref: 'payment-success', title: 'success', body: 'payment_success'},
				{ref: 'stripe-subs-error', title: 'payment_error', body: 'payment_error_message', type: -1},
				{ref: 'stripe-subs-success', title: 'success', body: 'stripe_subs_success', duration: 8000},
				{ref: 'stripe-identity-success', title: 'success', body: 'stripe_identity_success', duration: 8000},
				{ref: 'stripe-identity-processing', title: 'success', body: 'stripe_identity_verification_processing', duration: 8000},
				{ref: 'stripe-identity-error', title: 'error', body: 'stripe_identity_error', duration: 8000},
				{ref: 'stripe-payout-delay-success', title: 'success', body: 'stripe_update_delay_success', duration: 8000},
				{ref: 'stripe-instant-payout-not-eligible', title: 'error', body: 'stripe_instant_payout_not_eligible'},
				{ref: 'stripe-instant-payout-success', title: 'success', body: 'stripe_instant_payout_success', duration: 8000},
				{ref: 'stripe-instant-payout-error', title: 'error', body: 'stripe_instant_payout_error'},
				{ref: 'stripe-instant-payout-invalid-amount', title: 'error', body: 'stripe_instant_payout_invalid_amount'},
				{ref: 'nothing-to-save', title: 'warning', body: 'nothing_to_save'},
				{ref: 'missing-valid-address', title: 'error', body: 'add_valid_address'},
				{ref: 'error-customer-type', title: 'error', body: 'error_customer_type'},
				{ref: 'warning-customer-missing', title: 'warning', body: 'warning_customer_missing'},
				{ref: 'success-customer-linked', title: 'success', body: 'success_customer_linked'},
				{ref: 'error-credit-mismatch', title: 'error', body: 'error_credit_mismatch'},
				{ref: 'error-low-credit', title: 'error', body: 'error_low_credit'},
				{ref: 'delete-customer-open-orders-error', title: 'error', body: 'error_customer_delete_open_orders'},
				{ref: 'delete-customer-later-orders-error', title: 'error', body: 'error_customer_delete_later_orders'},
				{ref: 'delete-customer-credit-error', title: 'error', body: 'error_customer_delete_credit'},
				{ref: 'delete-customer-debit-error', title: 'error', body: 'error_customer_delete_debit'},
				{ref: 'name-required', title: 'error', body: 'name_required'},
				{ref: 'no-changes', title: 'info', body: 'no_changes'},
				{ref: 'short-pos-code', title: 'error', body: 'short_pos_code'},
				{ref: 'wrong-pos-code', title: 'error', body: 'wrong_pos_code', duration: 6000},
				{ref: 'incomplete-new-terminal-warning', title: 'warning', body: 'incomplete_new_terminal'},
				{ref: 'pos-settings-warn', title: 'warning', body: 'popup_link_warn'},
				{ref: 'pos-payments-warn', title: 'warning', body: 'popup_stripe_warn'},
				{ref: 'pos-status-updated', title: 'saved', body: 'pos_updated'},
				{ref: 'delivery-status-updated', title: 'saved', body: 'delivery_updated'},
				{ref: 'takeaway-status-updated', title: 'saved', body: 'takeaway_updated'},
				{ref: 'delivery-status-no-address', title: 'error', body: 'no_address_delivery'},
				{ref: 'link-invalid', title: 'error', body: 'link_invalid', duration: 8000},
				{ref: 'link-already-exists', title: 'error', body: 'link_already_exists'},
				{ref: 'link-not-editable', title: 'error', body: 'link_not_editable'},
				{ref: 'room-name-empty', title: 'error', body: 'room_name_empty'},
				{ref: 'qr-quantity-invalid', title: 'error', body: 'qr_quantity_invalid'},
				{ref: 'ade-connected', title: 'success', body: 'ade_connected'},
				{ref: 'link-copied', title: 'success', body: 'link_copied'},
				{ref: 'info-copied', title: 'success', body: 'info_copied'},
				{ref: 'link-in-console', title: 'success', body: 'link_shown_in_console'},
				{ref: 'failed-ade-login', title: 'error', body: 'failed_ade_login'},
				{ref: 'error-ade-fields', title: 'error', body: 'error_ade_fields'},
				{ref: 'payments-fail', title: 'error', body: 'cash_payment_warn', duration: 6000},
				{ref: 'sms-payments-fail', title: 'error', body: 'sms_payment_warn', duration: 6000},
				{ref: 'sms-cash-payments-fail', title: 'error', body: 'sms_cash_payment_warn', duration: 6000},
				{ref: 'too-many-users', title: 'error', body: 'too_many_users', duration: 12000},
				{ref: 'max-users-edit', title: 'error', body: 'too_many_edit_users', duration: 6000},
				{ref: 'max-webhooks-edit', title: 'error', body: 'too_many_webhooks_plan', duration: 6000},
				{ref: 'user-currency-mismatch', title: 'error', body: 'currency_mismatch', duration: 10000},
				{ref: 'no-webhooks-edit', title: 'error', body: 'no_webhook_plan', duration: 6000},
				{ref: 'cannot-change-update', title: 'error', body: 'cannot_change_unpaid', duration: 10000},
				{ref: 'webhook-cant', title: 'error', body: 'cant_add_webhook_plan', duration: 6000},
				{ref: 'webhook-zero', title: 'error', body: 'zero_webhook_plan', duration: 6000},
				{ref: 'invalid-payment-method', title: 'error', body: 'invalid_payment_method'},
				{ref: 'test-print-success', title: 'printing_in_progress', body: 'printing_test_ongoing', duration: 6000},
				{ref: 'print-success', title: 'success', body: 'print_success'},
				{ref: 'print-reservation-success', title: 'success', body: 'print_reservation_success'},
				{ref: 'print-reservations-success', title: 'success', body: 'print_reservations_success'},
				{ref: 'print-customer-credit-success', title: 'success', body: 'print_customer_credit_success'},
				{ref: 'success-saved-x-coupon-codes', title: 'success', body: 'success_saved_x_coupon_codes', duration: 6000},
				{ref: 'satispay-code-empty', title: 'error', body: 'satispay_code_empty'},
				{ref: 'voucherly-code-empty', title: 'error', body: 'voucherly_code_empty'},
				{ref: 'manual-code-length', title: 'error', body: 'manual_code_length'},
				{ref: 'total-too-high', title: 'error', body: 'total_too_high'},
				{ref: 'total-too-low', title: 'error', body: 'total_too_low'},
				{ref: 'stripe-not-connected', title: 'warning', body: 'stripe_not_connected_warning'},
				{ref: 'stripe-payments-not-active', title: 'error', body: 'stripe_payments_not_active', duration: 12000},
				{ref: 'menu-generation-empty-items', title: 'error', body: 'no_items_found_generation', duration: 8000},
				{ref: 'menu-generation-success-items', title: 'success', body: 'x_items_menu_generated'},
				{ref: 'missing-business-link', title: 'warning', body: 'stripe_set_link_warning'},
                {ref: 'stripe-migration-needed', title: 'warning', body: 'stripe_migration_needed', duration: 8000},
                {ref: 'refunded-transaction-success', title: 'success', body: 'transaction_refunded', type: 1},
				{ref: 'refund-limit-exceeded-error', title: 'error', body: 'refund_limit_exceeded_error'},
				{ref: 'satispay-code-invalid', title: 'error', body: 'satispay_code_invalid'},
				{ref: 'voucherly-code-invalid', title: 'error', body: 'voucherly_code_invalid'},
				{ref: 'voucherly-no-gateways', title: 'warning', body: 'voucherly_no_gateways', duration: 10000},
				{ref: 'permission-denied', title: 'error', body: 'permission_denied'},
				{ref: 'permission-denied-order-creation', title: 'error', body: 'permission_denied_order_creation'},
				{ref: 'permission-denied-order-edit', title: 'error', body: 'permission_denied_order_edit'},
				{ref: 'fiscal-receipt-error', title: 'error', body: 'fiscal_receipt_error', duration: 6000},
				{ref: 'fiscal-receipt-error-code', title: 'error', body: 'fiscal_receipt_error_code', duration: 6000},
				{ref: 'fiscal-receipt-error-not-now', title: 'error', body: 'fiscal_receipt_error_not_now', duration: 6000},
				{ref: 'fiscal-receipt-error-offline', title: 'error', body: 'fiscal_receipt_error_offline', duration: 6000},
				{ref: 'fiscal-close-all-orders', title: 'error', body: 'fiscal_close_all_orders'},
				{ref: 'plugins-updated', title: 'subscription_updated', body: 'subscription_updated_success', type: 1},
				{ref: 'forbidden-permission', title: 'warning', body: 'forbidden_section_plugins'},
				{ref: 'forbidden-missing-functionality', title: 'warning', body: 'section_x_sub_y'},
				{ref: 'forbidden-missing-subscription', title: 'warning', body: 'section_x_sub'},
				{ref: 'plan-locked-generic', title: 'warning', body: 'plan_locked_feature'},
				{ref: 'fetch-events-error', title: 'error', body: 'fetch_events_error', duration: 6000},
				{ref: 'linked-ticket-required', title: 'warning', body: 'linked_ticket_required', duration: 6000},
				{ref: 'already-generating-menu', title: 'warning', body: 'already_generating_menu', duration: 6000},
				{ref: 'no-key-inserted', title: 'warning', body: 'no_key_inserted', duration: 6000},
				{ref: 'wrong-api-key', title: 'error', body: 'wrong_api_key', duration: 6000},
				{ref: 'menu-fail-rate-limit', title: 'error', body: 'menu_fail_rate_limit'},
				{ref: 'duplicated-shortcuts', title: 'error', body: 'duplicated_shortcuts'},
				{ref: 'too-many-shortcuts', title: 'error', body: 'too_many_shortcuts'},
				{ref: 'not-unique-tips', title: 'error', body: 'not_unique_tips'},
				{ref: 'tips-saved', title: 'saved', body: 'tips_saved'},
				{ref: 'no-mass-delete', title: 'error', body: 'no_mass_delete'},
				{ref: 'tips-saved', title: 'saved', body: 'tips_saved'},
				{ref: 'ai-images-rate-limit', title: 'error', body: 'ai_images_rate_limit'},
				{ref: 'ai-images-too-many', title: 'error', body: 'ai_images_too_many'},
				{ref: 'newsletter-pending', title: 'warning', body: 'newsletter_pending_message', duration: 6000},
				{ref: 'newsletter-send-started', title: 'success', body: 'newsletter_send_started_message', duration: 6000},
				{ref: 'newsletter-content-rejected', title: 'error', body: 'newsletter_content_rejected_message', duration: 12000},
				{ref: 'newsletter-max-images', title: 'error', body: 'newsletter_max_images', duration: 6000},
				{ref: 'newsletter-not-draft', title: 'error', body: 'newsletter_not_draft', duration: 6000},
				{ref: 'select-at-least-one-section', title: 'warning', body: 'select_at_least_one_section_message', duration: 5000},
				{ref: 'no-recipients-found', title: 'warning', body: 'no_recipients_found_message', duration: 5000},
				{ref: 'business-link-not-configured', title: 'error', body: 'business_link_not_configured_message', duration: 6000},
				{ref: 'insufficient-credits-for-newsletter', title: 'error', body: 'insufficient_credits_for_newsletter', duration: 7000},
				{ref: 'invalid-business-type', title: 'error', body: 'invalid_business_type', duration: 6500},
				{ref: 'vat-nature-not-set', title: 'error', body: 'vat_nature_not_set'},
				{ref: 'invalid-pin-format', title: 'error', body: 'invalid_pin_format'},
				{ref: 'funnel-menu-load-fail', title: 'error_loading_menu', body: 'error_loading_menu_text', type: -2, duration: 6500},
				{ref: 'need-permissions', title: 'warning', body: 'need_permissions', duration: 7000},
				{ref: 'webhook-type-zero', title: 'error', body: 'webhook_type_zero'},
				{ref: 'sms-missing-phone', title: 'error', body: 'sms_missing_phone'},
				{ref: 'sms-missing-type', title: 'error', body: 'sms_missing_type'},
				{ref: 'sms-failed-charge', title: 'error', body: 'sms_failed_charge'},
				{ref: 'webhook-deleted', title: 'success', body: 'webhook_deleted'},
				{ref: 'no-receipt-single-item', title: 'warning', body: 'bottom_payment_no_receipt_single_item'},
				{ref: 'max-5000-coupon-codes', title: 'warning', body: 'max_5000_coupon_codes'},
				{ref: 'google-integration-subscription-required', title: 'error', body: 'google_integration_subscription_required', duration: 8000},
				{ref: 'google-integration-experience-required', title: 'error', body: 'google_integration_experience_required', duration: 8000},
				{ref: 'z-rep-reinjest-wrong-printer-error', title: 'error', body: 'z_rep_reinjest_wrong_printer_error'},
				{ref: 'byo-analytics-subscription-required', title: 'error', body: 'byo_analytics_subscription_required', duration: 8000},
				{ref: 'byo-analytics-multiple-enterprise-required', title: 'error', body: 'byo_analytics_multiple_enterprise_required', duration: 8000},
				{ref: 'no-day-selected-to-print', title: 'error', body: 'no_day_selected_to_print'},
				{ref: 'no-reservation-to-print', title: 'error', body: 'no_reservation_to_print'},
				{ref: 'file-type-not-supported', title: 'error', body: 'file_type_not_supported'},
				{ref: 'item-already-redeemed', title: 'redemption_failed', body: 'item_already_redeemed', type: -2, duration: 8000},
				{ref: 'item-not-found', title: 'redemption_failed', body: 'item_not_found', type: -2, duration: 8000},
				{ref: 'byo-analytics-multiple-enterprise-required', title: 'error', body: 'byo_analytics_multiple_enterprise_required', duration: 8000},
				{ref: 'please-provide-more-details', title: 'warning', body: 'please_provide_more_details', duration: 6000},
				{ref: 'test-email-sent', title: 'success', body: 'test_email_sent'},
				{ref: 'qr-code-not-recognized', title: 'warning', body: 'qr_code_not_recognized', type: -1},
			]
		},
	},
	methods: {
		typeFromObject(notificationObject) {
			if(notificationObject.type) {
				return notificationObject.type;
			}
			if (notificationObject.title === 'error') {
				return -2;
			} else if (notificationObject.title === 'warning') {
				return -1;
			} else if (notificationObject.title === 'saved' || notificationObject.title === 'success') {
				return 1;
			}
			return 0;

		},
		openNotification(ref, bodyParams) {
			let notification = this.notifications.find(notification => notification.ref === ref);
			if(notification) {
				let hadSameParams = ((!bodyParams && !notification?.bodyParams) || (notification?.bodyParams && bodyParams && JSON.stringify(notification?.bodyParams) === JSON.stringify(bodyParams)))
				if(notification.hasOwnProperty('exitTimeout')) {
					clearTimeout(notification.exitTimeout);
				}
				let alreadyActive = this.activeNotifications.find(n => n.ref === ref);
				if(!alreadyActive) {
					if(bodyParams && !hadSameParams) {
						notification.bodyParams = bodyParams;
					}
					this.activeNotifications.push(notification);
				} else if(!hadSameParams) {
					if(bodyParams) {
						notification.bodyParams = bodyParams;
					}
					clearTimeout(notification.exitTimeout);
					this.activeNotifications = this.activeNotifications.filter(n => n.ref !== ref);
					this.activeNotifications.push(notification);
				}
				this.$nextTick(() => {
					setTimeout(() => {
						if(this.$refs && this.$refs[ref] && notification) {
							let notificationComponent = this.$refs[ref].length ? this.$refs[ref][0] : this.$refs[ref];
							if(notificationComponent.openNotification) {
								let duration = notificationComponent.duration;
								notificationComponent.openNotification();
								notification.exitTimeout = setTimeout(() => {
									if(notification) {
										this.activeNotifications = this.activeNotifications.filter(n => n.ref !== ref);
									}
								}, duration+1000);
							}
						}
					}, 30);
				});
			}
		}
	},
	mounted () {
		for(let i = 0; i < this.notifications.length; i++) {
			this.notificationStore.notifications.push({
				id: this.notifications[i].ref,
				open: (bodyParams) => this.openNotification(this.notifications[i].ref, bodyParams),
			});
		}
	},
}
</script>

<style scoped>

</style>